import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { getCurrencySymbol, detectUserCountryCode } from "@/utils/currency";
import api from "@/utils/api";
import { ArrowLeft, Crown, Zap, Users, MessageCircle, Share2 } from "lucide-react";
import { PlanCard, tr } from "@/components/subscription/PlanCard";
import { PromoCodeCard } from "@/components/subscription/PromoCodeCard";

const PRICES = { free: 0, monthly: 9.99, yearly: 99.99 };

const getPlans = (currencySymbol) => ({
  en: {
    free: { name: "Free", price: `${currencySymbol}0`, priceValue: 0, period: "forever",
      features: [ "1 cycle generation per month", "Basic peptide recommendations", "Beginner's guide", "10% off at Summit Peptides"],
      cta: "Current Plan" },
    monthly: { name: "Premium Monthly", price: `${currencySymbol}${PRICES.monthly}`, priceValue: PRICES.monthly, period: "/month",
      features: [ "Unlimited cycle generations", "★ 30% OFF at Summit Peptides (vs 10% for free)", "Custom peptide cycles", "PDF export with checkboxes", "Share cycles via email",
        "Invite collaborators", "1-on-1 consultation booking", "Priority AI recommendations", "Credit/debit card on file · Cancel anytime — no penalty"],
      cta: "Subscribe Monthly" },
    yearly: { name: "Premium Yearly", price: `${currencySymbol}${PRICES.yearly}`, priceValue: PRICES.yearly, period: "/year",
      savings: "Save 17%", features: [ "Everything in Monthly", "★ 30% OFF at Summit Peptides all year", "2 months FREE", "Priority support", "Early access to new features"],
      cta: "Subscribe Yearly" }
  },
  pt: {
    free: { name: "Grátis", price: `${currencySymbol}0`, priceValue: 0, period: "para sempre",
      features: [ "1 ciclo por mês", "Recomendações básicas", "Guia para iniciantes", "10% off na Summit Peptides"],
      cta: "Plano Atual" },
    monthly: { name: "Premium Mensal", price: `${currencySymbol}${PRICES.monthly}`, priceValue: PRICES.monthly, period: "/mês",
      features: [ "Ciclos ilimitados", "★ 30% OFF na Summit Peptides (vs 10% grátis)", "Ciclos com peptídeos personalizados", "Exportar PDF com checkboxes", "Compartilhar ciclos por email",
        "Convidar colaboradores", "Agendar consulta 1-a-1", "Recomendações IA prioritárias", "Cartão de crédito/débito no arquivo · Cancele quando quiser — sem multa"],
      cta: "Assinar Mensal" },
    yearly: { name: "Premium Anual", price: `${currencySymbol}${PRICES.yearly}`, priceValue: PRICES.yearly, period: "/ano",
      savings: "Economize 17%", features: [ "Tudo do Mensal", "★ 30% OFF Summit Peptides o ano todo", "2 meses GRÁTIS", "Suporte prioritário", "Acesso antecipado a novidades"],
      cta: "Assinar Anual" }
  },
  es: {
    free: { name: "Gratis", price: `${currencySymbol}0`, priceValue: 0, period: "para siempre",
      features: [ "1 ciclo por mes", "Recomendaciones básicas", "Guía para principiantes", "10% off en Summit Peptides"],
      cta: "Plan Actual" },
    monthly: { name: "Premium Mensual", price: `${currencySymbol}${PRICES.monthly}`, priceValue: PRICES.monthly, period: "/mes",
      features: [ "Ciclos ilimitados", "★ 30% OFF en Summit Peptides (vs 10% gratis)", "Ciclos con péptidos personalizados", "Exportar PDF con checkboxes", "Compartir ciclos por email",
        "Invitar colaboradores", "Reservar consulta 1-a-1", "Recomendaciones IA prioritarias", "Tarjeta de crédito/débito en archivo · Cancela cuando quieras — sin penalización"],
      cta: "Suscribirse Mensual" },
    yearly: { name: "Premium Anual", price: `${currencySymbol}${PRICES.yearly}`, priceValue: PRICES.yearly, period: "/año",
      savings: "Ahorra 17%", features: [ "Todo del Mensual", "★ 30% OFF Summit Peptides todo el año", "2 meses GRATIS", "Soporte prioritario", "Acceso anticipado a novedades"],
      cta: "Suscribirse Anual" }
  }
});

const featureIconDef = (language) => [
  { Icon: Zap, bg: 'bg-cyan-500/20', color: 'text-cyan-400',
    title: tr(language, "Unlimited Cycles", "Ciclos Ilimitados", "Ciclos Ilimitados"),
    subtitle: tr(language, "Generate as many as you want", "Gere quantos quiser", "Genera cuantos quieras") },
  { Icon: Share2, bg: 'bg-violet-500/20', color: 'text-violet-400',
    title: tr(language, "Share Cycles", "Compartilhar", "Compartir"),
    subtitle: tr(language, "Send to friends", "Envie para amigos", "Envía a amigos") },
  { Icon: Users, bg: 'bg-green-500/20', color: 'text-green-400',
    title: tr(language, "Collaborators", "Colaboradores", "Colaboradores"),
    subtitle: tr(language, "Work as a team", "Trabalhe em equipe", "Trabaja en equipo") },
  { Icon: MessageCircle, bg: 'bg-amber-500/20', color: 'text-amber-400',
    title: tr(language, "1-on-1 Consults", "Consultas 1-a-1", "Consultas 1-a-1"),
    subtitle: tr(language, "Personalized support", "Suporte personalizado", "Soporte personalizado") },
];

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const { language } = useLanguage();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [userCountryCode, setUserCountryCode] = useState('+1');

  const currencySymbol = getCurrencySymbol(userCountryCode);
  const plans = getPlans(currencySymbol);
  const currentPlans = plans[language] || plans.en;

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await api.get('/subscription/status', { headers: getAuthHeader() });
      setSubscriptionStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(tr(language, "Could not check subscription status", "Erro ao verificar assinatura", "Error al verificar suscripción"));
      }
    }
  }, [getAuthHeader, language]);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const response = await api.get(`/subscription/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success(tr(language, "Subscription activated successfully!", "Assinatura ativada com sucesso!", "¡Suscripción activada con éxito!"));
        fetchSubscriptionStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not verify payment status");
    }
  }, [language, fetchSubscriptionStatus]);

  useEffect(() => {
    const detectedCode = detectUserCountryCode();
    setUserCountryCode(detectedCode);
    const storedCode = localStorage.getItem('user_country_code');
    if (storedCode) setUserCountryCode(storedCode);
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) checkPaymentStatus(sessionId);
    if (isAuthenticated) fetchSubscriptionStatus();
  }, [searchParams, isAuthenticated, checkPaymentStatus, fetchSubscriptionStatus]);

  const handleSubscribe = async (planId, isTrial = false) => {
    if (!isAuthenticated) {
      toast.error(tr(language, "Please login first", "Faça login primeiro", "Inicie sesión primero"));
      navigate("/");
      return;
    }

    setLoadingPlan(planId);
    try {
      let response;
      if (isTrial) {
        // 30-day free trial is now delivered via the promo system (LAUNCH30).
        response = await api.post('/promo/redeem', {
          code: 'LAUNCH30',
          plan_id: planId,
          origin_url: window.location.origin,
        }, { headers: getAuthHeader() });
      } else {
        response = await api.post('/subscription/checkout', {
          plan_id: planId,
          origin_url: window.location.origin,
        }, { headers: getAuthHeader() });
      }
      window.location.href = response.data.checkout_url;
    } catch (error) {
      // Backends may return a structured detail (dict) — never pass an object to toast.
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || error.message || "Failed to start checkout");
      toast.error(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  const isPremium = subscriptionStatus?.is_premium;
  const trialUsed = subscriptionStatus?.trial_used;
  const featureIcons = featureIconDef(language);

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-home"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-white">
                {tr(language, "Plans & Pricing", "Planos e Preços", "Planes y Precios")}
              </h1>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
            <Crown className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-400 font-medium">
              {tr(language, "Unlock Full Potential", "Desbloqueie Todo o Potencial", "Desbloquea Todo el Potencial")}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {tr(language, "Choose Your Plan", "Escolha Seu Plano", "Elige Tu Plan")}
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            {tr(language,
              "Start free or upgrade to Premium for unlimited cycles, sharing, and personalized consultations.",
              "Comece grátis ou faça upgrade para Premium para ciclos ilimitados, compartilhamento e consultas personalizadas.",
              "Comienza gratis o actualiza a Premium para ciclos ilimitados, compartir y consultas personalizadas.")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PlanCard variant="free" plan={currentPlans.free} language={language} isPremium={isPremium} trialUsed={trialUsed} loadingPlan={loadingPlan} onSubscribe={handleSubscribe} />
          <PlanCard variant="monthly" plan={currentPlans.monthly} language={language} isPremium={isPremium} trialUsed={trialUsed} loadingPlan={loadingPlan} onSubscribe={handleSubscribe} />
          <PlanCard variant="yearly" plan={currentPlans.yearly} language={language} isPremium={isPremium} trialUsed={trialUsed} loadingPlan={loadingPlan} onSubscribe={handleSubscribe} />
        </div>

        {!isPremium && (
          <div className="mt-10 max-w-xl mx-auto">
            <PromoCodeCard
              language={language}
              planId="monthly"
              getAuthHeader={getAuthHeader}
              isAuthenticated={isAuthenticated}
              onAuthRequired={() => navigate("/")}
            />
          </div>
        )}

        <div className="mt-16">
          <h3 className="text-2xl font-bold text-white text-center mb-8">
            {tr(language, "Premium Features", "Recursos Premium", "Características Premium")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featureIcons.map((f) => (
              <div key={f.title} className="text-center">
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mx-auto mb-3`}>
                  <f.Icon className={`w-6 h-6 ${f.color}`} />
                </div>
                <h4 className="text-white font-medium mb-1">{f.title}</h4>
                <p className="text-sm text-zinc-500">{f.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
